import { ApolloClient, gql } from '@apollo/client'
import { UserScopedGrowthBook } from '@growthbook/growthbook'
import { ArgosClient } from '@jusbrasil-web/shared-argos-client'
import { parseUserCookie, UserSession } from '@jusbrasil-web/shared-user-session'
import { GetServerSidePropsContext } from 'next'
import { UAParser } from 'ua-parser-js'

import {
  EXPERIMENT_PARTICIPATE_EVENT_NAME,
  FEATURE_FLAGS_INTERNAL_STATE,
  NETWORK_TIMEOUT_MS,
} from '../constants'
import {
  AppPropsWithFeatureFlags,
  FeatureValues,
  TrackedExperiments,
  UserData,
  WithFeatureFlags,
} from '../types'
import { GetProfileForFeatureFlagsQuery } from './__generated__/with-feature-flags.graphql'
import { getClient } from './client'

const argosClient = new ArgosClient()
const enableDevMode = process.env.NEXT_PUBLIC_GROWTHBOOK_DEV_MODE === 'true'

const SNAKE_CASE_PATTERN = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/

function validateFeatureKeys(keys: string[]) {
  const invalid = keys.filter((key) => !SNAKE_CASE_PATTERN.test(key))
  if (invalid.length > 0) {
    throw new Error(
      `[shared-feature-flags] feature keys must be lowercase snake_case. Invalid: ${invalid.join(
        ', '
      )}`
    )
  }
}

const GET_PROFILE_FOR_FEATURE_FLAGS = gql`
  query getProfileForFeatureFlags {
    root {
      viewer: me {
        pid
        isLawyer
        user {
          email
          isAdmin
        }
      }
    }
  }
`

async function getUserData(
  ctx: GetServerSidePropsContext,
  apolloClient: ApolloClient<unknown>
): Promise<UserData> {
  const userSession: UserSession = parseUserCookie(ctx)
  const device = new UAParser(ctx.req?.headers?.['user-agent'] ?? '').getDevice()
  const deviceType = device.type || 'desktop'
  if (!userSession?.pid) {
    return {
      is_admin: false,
      is_lawyer: false,
      device_id: userSession?.jdid,
      device_type: deviceType,
    }
  }

  try {
    const { data } = await apolloClient.query<GetProfileForFeatureFlagsQuery>({
      query: GET_PROFILE_FOR_FEATURE_FLAGS,
      context: { fetchOptions: { signal: AbortSignal.timeout(NETWORK_TIMEOUT_MS) } },
    })
    const viewer = data?.root?.viewer
    if (viewer) {
      const { pid, user } = viewer
      return {
        pid: String(pid),
        device_id: userSession.jdid,
        email: user?.email,
        is_admin: !!user?.isAdmin,
        is_lawyer: !!viewer.isLawyer,
        device_type: deviceType,
      }
    }
  } catch (e) {
    console.error(
      '[shared-feature-flags] failed to load viewer profile, falling back to cookie attrs',
      e
    )
  }

  return {
    pid: String(userSession.pid),
    device_id: userSession.jdid,
    is_admin: false,
    is_lawyer: false,
    device_type: deviceType,
  }
}

export const withFeatureFlags: WithFeatureFlags = async (
  ctx,
  apolloClient,
  featuresWithDefaultValue,
  options
) => {
  type F = typeof featuresWithDefaultValue

  validateFeatureKeys(Object.keys(featuresWithDefaultValue))

  const [userData, client] = await Promise.all([getUserData(ctx, apolloClient), getClient()])
  const jdid = userData.device_id

  const trackedExperiments: TrackedExperiments = {}
  const instance: UserScopedGrowthBook<typeof featuresWithDefaultValue> =
    client.createScopedInstance({
      enableDevMode,
      attributes: { ...options?.extraUserAttributes, ...userData },
      trackingCallback: (experiment, result) => {
        // Key by the feature id (not experiment.key): getFeatureValue and evalFeature
        // operate on feature keys, and a feature key can differ from its experiment
        // key. featureId falls back to experiment.key for inline experiments that
        // aren't backed by a feature flag.
        const featureKey = result.featureId ?? experiment.key
        trackedExperiments[featureKey] = {
          experimentKey: experiment.key,
          variationName: result.name ?? null,
        }
      },
    })

  const featureFlags: F = { ...featuresWithDefaultValue }
  for (const key in featuresWithDefaultValue) {
    if (Object.prototype.hasOwnProperty.call(featuresWithDefaultValue, key)) {
      const featureResult = instance.evalFeature(key)
      featureFlags[key] = featureResult.value ?? featuresWithDefaultValue[key]
    }
  }

  // Seed with the declared (static) flags so getFeatureValues always returns
  // every static flag plus any pattern match. The dynamic keys aren't part of
  // the typed `featureFlags` map, hence the wider `FeatureValues` type.
  //
  // When a key pattern is provided, evaluate every matching GrowthBook feature
  // and capture its resolved value. evalFeature also fires the tracking
  // callback, populating trackedExperiments for participation. Matching happens
  // before evalFeature so flags outside the pattern are never evaluated/tracked.
  const matchedFeatureValues: FeatureValues = { ...featureFlags }
  const keyPattern = options?.featureFlagsKeyPattern
  if (keyPattern) {
    try {
      const features = client.getFeatures() ?? {}
      for (const key of Object.keys(features)) {
        if (!keyPattern.test(key)) continue
        const { value } = instance.evalFeature(key)
        if (value != null) {
          matchedFeatureValues[key] = value
        }
      }
    } catch (e) {
      console.error('[shared-feature-flags] failed to evaluate pattern-matched features', e)
    }
  }

  const sentExperiments = new Set<string>()

  // Sends the Argos participation event for a feature key, at most once per
  // request. A no-op unless GrowthBook actually bucketed the user into an
  // experiment for that key (i.e. the tracking callback recorded it).
  const sendParticipation = (key: string) => {
    const tracked = trackedExperiments[key]
    if (tracked && !sentExperiments.has(key)) {
      sentExperiments.add(key)
      argosClient.send(userData.pid, EXPERIMENT_PARTICIPATE_EVENT_NAME, {
        experiment: tracked.experimentKey,
        alternative: tracked.variationName,
        device_id: jdid ?? null,
      })
    }
  }

  return {
    getFeatureValue(key) {
      const value = featureFlags[key]
      sendParticipation(key)
      return value
    },
    inExperiment(key) {
      // Read-only: reports bucketing without firing the participation event.
      return Boolean(trackedExperiments[key])
    },
    getFeatureValues() {
      // getFeatureValues only serves the dynamic, pattern-matched case. When the
      // keys are known (declared in `featuresWithDefaultValue`), the preferred
      // way to read a feature is getFeatureValue(key).
      if (!keyPattern) {
        throw new Error(
          '[shared-feature-flags] getFeatureValues requires the `featureFlagsKeyPattern` ' +
            'option. For known feature keys, use getFeatureValue(key) instead.'
        )
      }
      for (const key of Object.keys(matchedFeatureValues)) {
        sendParticipation(key)
      }
      return matchedFeatureValues
    },
    withFeatureFlagsProps() {
      const props: AppPropsWithFeatureFlags<F> = {
        [FEATURE_FLAGS_INTERNAL_STATE]: {
          featureFlags,
          trackedExperiments,
        },
      }
      return props
    },
  }
}
